import { useState } from 'react';
import { Controller, type Control, useWatch, type Path } from 'react-hook-form';
import {
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconRefresh,
} from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import { Textarea } from '@/admin/components/ui/textarea';
import { CollapsibleSection } from './collapsible-section';
import { SocialPreviewGrid } from './social-preview-card';
import { CodeEditor } from '@/admin/components/code-editor';
import { cn } from '@/admin/lib/utils';

// Interface for SEO fields that must exist in the form data
export interface SeoFields {
  seo_title: string;
  seo_description: string;
  seo_image: string;
  seo_keywords: string;
  canonical_url: string;
}

interface SeoSectionProps<T extends SeoFields> {
  control: Control<T>;
  onSaveSeo: () => void;
  savingSeo: boolean;
  // Default values from item (skill/blog)
  defaultTitle: string;
  defaultDescription: string;
  defaultImage: string;
  itemName: string; // Used for "Using: itemName" text
  baseUrl?: string;
  urlPathPrefix?: string; // 'skills' or 'blog'
}

export const SeoSection = <T extends SeoFields>({
  control,
  onSaveSeo,
  savingSeo,
  defaultTitle,
  defaultDescription,
  defaultImage,
  itemName,
  baseUrl = 'https://cueboard.ai',
  urlPathPrefix = 'skills',
}: SeoSectionProps<T>) => {
  const [showTwitterOverride, setShowTwitterOverride] = useState(false);
  const [metaCodeCopied, setMetaCodeCopied] = useState(false);

  // Watch SEO fields for preview
  // We cast the field names to Path<T> because we know T extends SeoFields
  // but TypeScript needs help verifying the strings match keyof T
  const seoTitle = useWatch({ control, name: 'seo_title' as Path<T> });
  const seoDescription = useWatch({
    control,
    name: 'seo_description' as Path<T>,
  });
  const seoImage = useWatch({ control, name: 'seo_image' as Path<T> });

  // Computed effective values (used for preview)
  const effectiveTitle = seoTitle || defaultTitle || itemName;
  const effectiveDescription = seoDescription || defaultDescription || '';
  const effectiveImage = seoImage || defaultImage || '';
  const effectiveUrl = `${baseUrl}/${urlPathPrefix}/${itemName}`;

  // Generate meta tags code
  const generateMetaTags = () => {
    const tags = [
      '<!-- HTML Meta Tags -->',
      `<title>${effectiveTitle}</title>`,
      `<meta name="description" content="${effectiveDescription}">`,
      '',
      '<!-- OpenGraph Meta Tags -->',
      `<meta property="og:url" content="${effectiveUrl}">`,
      '<meta property="og:type" content="website">',
      `<meta property="og:title" content="${effectiveTitle}">`,
      `<meta property="og:description" content="${effectiveDescription}">`,
      effectiveImage
        ? `<meta property="og:image" content="${effectiveImage}">`
        : '',
      '',
      '<!-- Twitter Meta Tags -->',
      '<meta name="twitter:card" content="summary_large_image">',
      `<meta name="twitter:title" content="${effectiveTitle}">`,
      `<meta name="twitter:description" content="${effectiveDescription}">`,
      effectiveImage
        ? `<meta name="twitter:image" content="${effectiveImage}">`
        : '',
    ].filter(Boolean);

    return tags.join('\n');
  };

  const handleCopyMetaTags = () => {
    navigator.clipboard.writeText(generateMetaTags());
    setMetaCodeCopied(true);
    setTimeout(() => setMetaCodeCopied(false), 2000);
  };

  return (
    <CollapsibleSection
      id="seo"
      title="SEO & Social Sharing"
      description="OpenGraph, Twitter cards, and search engine optimization"
      defaultOpen={false}
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            onSaveSeo();
          }}
          disabled={savingSeo}
        >
          {savingSeo ? (
            <IconRefresh className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : (
            <IconCheck className="h-3.5 w-3.5 mr-1" />
          )}
          Save SEO
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="seo-title" className="text-xs">
              SEO Title
              <span className="text-muted-foreground ml-1">
                (og:title, twitter:title)
              </span>
            </Label>
            <Controller
              name={'seo_title' as Path<T>}
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    id="seo-title"
                    placeholder={defaultTitle || itemName || 'Custom title'}
                    className="h-9"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Recommended: 50-60 characters.{' '}
                    {field.value ? (
                      <span
                        className={
                          (field.value as string).length > 60
                            ? 'text-orange-500'
                            : ''
                        }
                      >
                        Current: {(field.value as string).length}
                      </span>
                    ) : (
                      `Using: "${defaultTitle || itemName}"`
                    )}
                  </p>
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="seo-description" className="text-xs">
              SEO Description
              <span className="text-muted-foreground ml-1">
                (og:description)
              </span>
            </Label>
            <Controller
              name={'seo_description' as Path<T>}
              control={control}
              render={({ field }) => (
                <>
                  <Textarea
                    {...field}
                    id="seo-description"
                    placeholder={
                      defaultDescription ||
                      'Custom description for search engines'
                    }
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Recommended: 110-160 characters.{' '}
                    {field.value ? (
                      <span
                        className={
                          (field.value as string).length > 160
                            ? 'text-orange-500'
                            : ''
                        }
                      >
                        Current: {(field.value as string).length}
                      </span>
                    ) : defaultDescription ? (
                      `Using item description`
                    ) : (
                      'No default description'
                    )}
                  </p>
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="seo-image" className="text-xs">
              SEO Image
              <span className="text-muted-foreground ml-1">(og:image)</span>
            </Label>
            <Controller
              name={'seo_image' as Path<T>}
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    id="seo-image"
                    placeholder={
                      defaultImage || 'https://... (1200x630 recommended)'
                    }
                    className="h-9"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {field.value
                      ? 'Using custom image'
                      : defaultImage
                        ? 'Using cover/hero image'
                        : 'No image set'}
                  </p>
                </>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seo-keywords" className="text-xs">
                Keywords
              </Label>
              <Controller
                name={'seo_keywords' as Path<T>}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="seo-keywords"
                    placeholder="ai, blog, automation"
                    className="h-9"
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="canonical-url" className="text-xs">
                Canonical URL
              </Label>
              <Controller
                name={'canonical_url' as Path<T>}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="canonical-url"
                    placeholder="https://..."
                    className="h-9"
                  />
                )}
              />
            </div>
          </div>

          {/* Twitter Override (Collapsible) */}
          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowTwitterOverride(!showTwitterOverride)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-muted/30 transition-colors"
            >
              <span className="font-medium">Twitter Override (optional)</span>
              <IconChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-muted-foreground transition-transform',
                  showTwitterOverride && 'rotate-180'
                )}
              />
            </button>
            {showTwitterOverride && (
              <div className="px-3 py-3 border-t space-y-3">
                <p className="text-[10px] text-muted-foreground">
                  Leave empty to use OpenGraph values
                </p>
                <div className="space-y-2">
                  <Label className="text-xs">Twitter Title</Label>
                  <Input
                    placeholder={effectiveTitle}
                    className="h-8 text-xs"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Twitter Description</Label>
                  <Input
                    placeholder={effectiveDescription.slice(0, 50) + '...'}
                    className="h-8 text-xs"
                    disabled
                  />
                </div>
                <p className="text-[10px] text-muted-foreground italic">
                  Twitter-specific fields coming soon
                </p>
              </div>
            )}
          </div>

          {/* Meta Tags Code */}
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b">
              <span className="text-xs font-medium">Meta Tags Code</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyMetaTags}
                className="h-6 text-xs px-2"
              >
                {metaCodeCopied ? (
                  <IconCheck className="h-3 w-3 mr-1" />
                ) : (
                  <IconCopy className="h-3 w-3 mr-1" />
                )}
                {metaCodeCopied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <CodeEditor value={generateMetaTags()} language="html" readOnly />
          </div>
        </div>

        {/* Right: Preview Cards */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-medium">Social Preview</h3>
            <span className="text-[10px] text-muted-foreground">
              How your page appears when shared
            </span>
          </div>
          <SocialPreviewGrid
            title={effectiveTitle}
            description={effectiveDescription}
            imageUrl={effectiveImage}
            url={effectiveUrl}
            siteName="Cueboard"
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};
